import { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import { ModuleType } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useGetKeyNote, useSaveKeyNote } from 'infrastructure/hooks/key-notes';
import { useQueryClient } from '@tanstack/react-query';
import {
  getFollowUpTableDataKey,
  getNewlyTableDataKey,
  getPartialsTableDataKey,
  useTableFiltersMap,
} from 'infrastructure/hooks/ccm/console';

import type { QueryKey } from '@tanstack/react-query';
import type { TTableName } from 'infrastructure/interfaces';

interface ISaveProps {
  note: string;
  expiresAt?: Date | null;
}
type ITablesKeys = {
  [K in TTableName]: (filters: any) => QueryKey;
};
export const CCMConsoleTablesKeys: ITablesKeys = {
  'follow-up': getFollowUpTableDataKey,
  newly: getNewlyTableDataKey,
  partials: getPartialsTableDataKey,
};

export const useDrawer = () => {
  const [message, setMessage] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [showExpired, setShowExpired] = useState<boolean>(false);
  const { isOpenDrawer, tableNote, updatedNote } = useAppSelector(
    (state) => state.ccmConsole,
  );
  const queryClient = useQueryClient();
  const { ccmConsole: store } = useAppDispatch();
  const { t } = useTranslation();

  const filters = useTableFiltersMap();

  const { saveNote, isLoading: saveNoteLoading } = useSaveKeyNote({
    module: ModuleType.CCM,
  });
  const patientGuid = tableNote?.patientGuid;
  const tableName = tableNote?.tableName;

  const reloadTable = () => {
    if (tableName) {
      queryClient.invalidateQueries(
        CCMConsoleTablesKeys[tableName](filters[tableName]),
      );
    }
  };
  const handleSave = ({ note, expiresAt }: ISaveProps) => {
    saveNote(
      {
        patientGuid: patientGuid ?? '',
        module: ModuleType.CCM,
        note,
        expiresAt: expiresAt || null,
        showExpired,
      },
      {
        onSuccess() {
          setOpenModal(false);
          notificationController.success({
            message: t('messages.keynoteAdded'),
          });
          reloadTable();
        },
      },
    );
  };

  const [, { data: ccmData, isFetching: ccmLoading }] = useGetKeyNote({
    module: ModuleType.CCM,
    patientGuid,
    canChangeModule: false,
    showExpired,
  });

  const handleCloseDrawer = () => {
    store.toggleOpenDrawer();
    setMessage('');
  };

  return {
    handleCloseDrawer,
    setOpenModal,
    setMessage,
    setShowExpired,
    handleSave,
    patientGuid,
    showExpired,
    saveNoteLoading,
    openModal,
    message,
    isOpenDrawer,
    updatedNote,
    tableNote,
    keynotes: ccmData,
    keynotesLoading: ccmLoading,
    reloadTable,
  };
};
